import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { Image } from 'src/app/people/models/image';
import { Tag } from 'src/app/people/models/tag';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { PersonEmailModel } from 'src/shared-app-portal/components/email-editor/person-email.model';

/** maps to PersonForMemberApplicationModel for preloading the person/team form */
export class PersonForMemberRequests {
  public id: string;
  public firstName: string;
  public lastName: string;
  public middleName: string;
  public suffix: string;
  public prefix: string;
  public fullName: string;
  public email: string;
  public linkedInProfile: string;
  public jobTitle: string;
  public company: string;
  public image: Image;
  public phoneNumber: string;
  public extension: string;
  public rating: number;
  public demographicInfo: DemographicInfo = new DemographicInfo();
  public tags: Tag[];
  public customFields: Attribute[] = new Array<Attribute>();
  public updatedOn: Date;
  public status: string;
  public secondaryEmails: PersonEmailModel[];
  public teamName: string;
  public teamPublicName: string;
  public selectedPositionPersonIds: Record<string, string[]>;
  /** positionDefinitionId -> personId -> seat field values already on that person's seat */
  public selectedPositionSeatFieldValues: Record<string, Record<string, Attribute[]>>;
  constructor(
    person: Partial<PersonForMemberRequests> & {
      customFields?: Attribute[];
    } = null
  ) {
    if (person) this.setProperties(person);
  }

  setImage(person: any) {
    if (person.image) {
      this.image = person.image;
    } else {
      this.image = new Image(person.imageId);
    }
  }

  isMaleGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'male'
    );
  }

  isFemaleGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'female'
    );
  }

  isNonBinaryGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'nonbinary'
    );
  }

  isUnknownGender() {
    return (
      !this.isMaleGender() &&
      !this.isFemaleGender() &&
      !this.isNonBinaryGender() &&
      !this.isPreferNotToAnswerGender()
    );
  }

  isPreferNotToAnswerGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'prefernottoanswer'
    );
  }

  getPhoneNumberForDisplay() {
    if (Number.parseInt(this.phoneNumber).toString().length === this.phoneNumber.length) {
      return `(${this.phoneNumber.substring(0, 3)}) ${this.phoneNumber.substring(
        3,
        6
      )}-${this.phoneNumber.substring(6, 10)}`;
    }

    return this.phoneNumber ? this.phoneNumber : '';
  }

  private setProperties(
    person: Partial<PersonForMemberRequests> & {
      customFields?: Attribute[];
    }
  ): void {
    this.id = person.id;
    this.firstName = person.firstName;
    this.lastName = person.lastName;
    this.middleName = person.middleName;
    this.suffix = person.suffix;
    this.prefix = person.prefix;
    this.fullName = this.getFullName(person);
    this.email = person.email;
    this.phoneNumber = person.phoneNumber;
    this.extension = person.extension;
    this.linkedInProfile = person.linkedInProfile;
    this.jobTitle = person.jobTitle;
    this.company = person.company;
    this.status = person.status;
    this.rating = person.rating;
    this.customFields = person.customFields?.map((attribute) => Attribute.parse(attribute)) ?? [];
    this.setImage(person);
    this.demographicInfo = new DemographicInfo(person.demographicInfo);
    this.tags = person.tags ? person.tags.map((tag) => new Tag(tag)) : [];
    this.updatedOn = person.updatedOn;
    this.secondaryEmails = person.secondaryEmails ?? [];
    this.teamName = person.teamName;
    this.teamPublicName = person.teamPublicName;
    this.selectedPositionPersonIds = person.selectedPositionPersonIds ?? {};
    this.selectedPositionSeatFieldValues = Object.fromEntries(
      Object.entries(person.selectedPositionSeatFieldValues ?? {}).map(([positionId, byPerson]) => [
        positionId,
        Object.fromEntries(
          Object.entries(byPerson ?? {}).map(([personId, values]) => [
            personId,
            (values ?? []).map((value) => Attribute.parse(value))
          ])
        )
      ])
    );
  }

  private getFullName(person: any): string {
    const nameParts = [];
    if (person.prefix) nameParts.push(person.prefix);
    if (person.firstName) nameParts.push(person.firstName);
    if (person.middleName) nameParts.push(person.middleName);
    if (person.lastName) nameParts.push(person.lastName);
    if (person.suffix) nameParts.push(person.suffix);
    return nameParts.join(' ');
  }
}
